// Muay Thai-specific taxonomy + constants for the Muay Thai Detailed
// Journal. Mirrors combatives.js (BJJ) shape so the same UI components
// (ChipInput, MultiSelectPills, MasteryPicker, etc.) reuse cleanly.
//
// . Locked schema per user spec — the user trains Muay
// Navarach / Muay Boran (self-defense/military lineage, not the modern
// sport). Vocabulary is intentionally traditional; if competition-style
// vocabulary needs to layer on later, add as alternates rather than
// replacing.

export const MT_CLASS_FORMATS = [
  'Pad work',
  'Bag work',
  'Shadow boxing',
  'Clinch',
  'Sparring',
  'Conditioning',
  'Comp Training',
];

export const MT_FOCUS_MODES = [
  'Drilling',
  'Technique class',
  'Live sparring',
  'Comp Training',
  'Open mat / Free training',
];

// Focus modes that should reveal the Sparring section.
export const MT_SPARRING_TRIGGERS = ['Live sparring', 'Comp Training'];

// Per-technique mastery — same 5-step scale as BJJ. Consistent vocabulary
// across surfaces because mastery is about the practitioner's
// relationship to a technique, not the art's structure.
export const MT_MASTERY_LEVELS = [
  { value: 'Studying', label: 'Studying', help: "Heard of it, watching video, not yet drilled" },
  { value: 'Drilling', label: 'Drilling', help: "Can do it slowly with a cooperative partner" },
  { value: 'Working', label: 'Working', help: "Landing it sometimes in flow / pad work" },
  { value: 'Hitting Live', label: 'Hitting Live', help: "Landing it in free sparring" },
  { value: 'Default Move', label: 'Default Move', help: "Reflexive, go-to weapon at this range" },
];

// Same felt scale across all combatives surfaces — same language gives
// the user a stable mental map across arts.
export const MT_FELT_SCALE = [
  { value: 1, label: 'Got smashed' },
  { value: 2, label: 'Held my own' },
  { value: 3, label: 'Even' },
  { value: 4, label: 'Pushed the pace' },
  { value: 5, label: 'Dominated' },
];

// Technique types — the "8 limbs" plus the elements that aren't direct
// strikes (clinch, defense, footwork, combinations). User picks one type
// per technique row. Selection drives nothing automatically; it's just a
// query-able tag.
export const MT_TECHNIQUE_TYPES = [
  'Punch',
  'Kick',
  'Knee',
  'Elbow',
  'Clinch',
  'Defense / Footwork',
  'Combination',
];

// Range / distance — striking's analogue to BJJ's position taxonomy.
// Long, mid, close, clinch. User picks where the technique lives.
export const MT_RANGES = [
  'Long',     // Kicking range
  'Mid',      // Boxing range
  'Close',    // Hooks, knees, elbows
  'Clinch',   // Plum / wrestling / Thai clinch
];

// Starter list for the per-technique name autocomplete. Traditional Muay
// Boran / Navarach + standard Muay Thai vocabulary mixed; user can type
// any custom name — this is suggestions only.
export const MT_COMMON_TECHNIQUES = [
  // Punches
  'Jab', 'Cross', 'Lead hook', 'Rear hook', 'Lead uppercut', 'Rear uppercut',
  'Overhand', 'Spinning backfist',
  // Kicks
  'Teep (push kick)', 'Roundhouse (low)', 'Roundhouse (body)', 'Roundhouse (head)',
  'Switch kick', 'Question mark kick', 'Axe kick', 'Side kick',
  'Spinning back kick', 'Spinning heel kick',
  // Knees
  'Straight knee', 'Diagonal knee', 'Curving knee', 'Jumping knee',
  'Clinch knee', 'Side knee',
  // Elbows
  'Horizontal elbow', 'Diagonal elbow (sok ti)', 'Uppercut elbow (sok ngat)',
  'Spinning elbow (sok klap)', 'Downward elbow (sok sap)',
  'Smash elbow (sok phung)',
  // Clinch
  'Plum control', 'Inside control', 'Outside control',
  'Clinch break', 'Sweep / dump', 'Clinch turn',
  // Defense / Footwork
  'Block (kick check)', 'Catch + sweep', 'Slip', 'Roll', 'Parry',
  'Long guard', 'Lateral step', 'Pivot', 'Cut angle',
];

// ---------- Factory helpers (mirror combatives.js for consistency) ----------

export const newMTTechniqueRow = () => ({
  type: '',              // Punch / Kick / Knee / Elbow / Clinch / Defense / Combination
  range: '',             // Long / Mid / Close / Clinch
  name: '',
  detail: '',
  reps: '',
  mastery: 'Drilling',
});

// MT sparring is simpler than BJJ — no submissions, no sweeps, no passes.
// Strikes count + felt + free note. Numbers stored as strings for
// placeholder UX (empty input shows "0" placeholder, doesn't snap back).
export const newMTSparring = () => ({
  rounds: '',
  partners: [],
  feltScale: 3,
  knockdownsLanded: '',
  knockdownsAbsorbed: '',
  note: '',
});

export const newMTReflection = () => ({
  intentHit: null,
  win: '',
  gap: '',
  nextFocus: '',
});

export const newMTInjury = () => ({ bodyPart: '', note: '' });
